import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, finalize, first, tap } from 'rxjs/operators';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { BankAccountService } from '../bank-account.service';
import { PartyService } from '../../../party/party.service';
import { Party } from '../../../party/party.model';
import { Bank } from "../bank.model";
import { BankAccount } from "../bank-account.model";
import { ChartOfAccounts } from "../../../chart-of-accounts/chart-of-accounts.model";
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToastModule } from "primeng/toast";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { SelectModule } from "primeng/select";
import { ButtonModule } from "primeng/button";
import { CommonModule } from '@angular/common';
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';

// PrimeNG Modules (additional ones based on common usage)
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";


@Component({
    selector: 'app-bank-account-entry',
    templateUrl: './bank-account-entry.component.html',
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ToastModule,
    BreadcrumbModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    FieldsetModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule
],
    providers: [
        BankAccountService,
        MessageService,
        SessionStorageService,
        PartyService
    ]
})
export class BankAccountEntryComponent implements OnInit {

    // --- Component State ---
    isLoading = false;
    editMode = false;
    isDialog = false;
    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    // --- Form ---
    bankAccountEntryForm: FormGroup;
    private bankAccountId: number | null;


    // --- Dropdown Data Sources ---
    banks: Bank[] = [];
    private party: Party;
    currencies: string[] = ['SAR', 'USD', 'EUR', 'GBP', 'AED', 'INR', 'JPY']; // Example currencies

    canSave = false;

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private bankAccountService: BankAccountService,
        private partyService: PartyService, // To fetch owners
        public dialogRef: DynamicDialogRef,
        public config: DynamicDialogConfig,
        private permissionService: PermissionService
    ) {
        this.bankAccountEntryForm = this.fb.group({
            accountName: ['', Validators.required],
            accountNumber: ['', Validators.required],
            iban: ['', Validators.required],
            currencyCode: ['SAR', Validators.required],
            bank: [null, Validators.required],
        });
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.loadInitialData().pipe(
            finalize(() => this.isLoading = false)
        ).subscribe(() => {
            // Check if we are running inside a dialog
            if (this.config?.data?.isDialog) {
                this.isDialog = true;
                this.prefillFromDialogData();
            } else {
                // Standard page behavior
                this.setupBreadcrumbs();
                this.handleRouting();
            }
        });

        if (this.editMode) {
            this.canSave = this.permissionService.hasPermission(app_permissions.accounts.bank_account_entry.edit);
        } else {
            this.canSave = this.permissionService.hasPermission(app_permissions.accounts.bank_account_entry.create);
        }

    }

    private prefillFromDialogData(): void {
        const { ownerType, ownerId, ownerName } = this.config.data;
        if (ownerType && ownerId && ownerName) {
            this.party = {
                id: {
                    entityId: ownerId,
                    entityType: ownerType
                },
                entityName: ownerName,
                isCompany: ownerType === 'Company',
                owningCompanyId: this.sessionStorageService.getCompany()?.id ?? 0
            };
        }
    }

    private handleRouting(): void {
        this.route.params.pipe(first()).subscribe(params => {
            if (params['id']) {
                this.editMode = true;
                this.bankAccountId = +params['id'];
                this.breadcrumbItems[2] = { label: `Edit Bank Account` };
                this.loadBankAccountData(this.bankAccountId);
            }
        });
    }

    private setupBreadcrumbs(): void {
        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Bank Accounts' }, { label: 'New Bank Account' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
    }

    private loadInitialData(): Observable<any> {
        const companyId = this.sessionStorageService.getCompany()?.id;
        const countryId = this.sessionStorageService.getCompany()?.countryId ?? 0;

        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company ID not found.' });
            return of(null);
        }

        return forkJoin({
            banks: this.bankAccountService.getBanksByCountryId(countryId).pipe(
                catchError(error => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load banks.' });
                    console.error('Bank load error:', error);
                    return of([]);
                })
            ),
        }).pipe(
            tap(response => {
                this.banks = response.banks;
            })
        );
    }

    private loadBankAccountData(id: number): void {
        this.isLoading = true;
        this.bankAccountService.getBankAccountById(id).pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (account) => {
                // Pre-load the parties for the owner type of the account being edited
                this.bankAccountEntryForm.patchValue({
                    ...account
                });
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Load Failed', detail: 'Could not retrieve bank account data.' });
                this.router.navigate(['/accounts/bank-accounts']);
            }
        });
    }

    saveBankAccount(): void {
        if (this.bankAccountEntryForm.invalid) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please fill all required fields.' });
            this.bankAccountEntryForm.markAllAsTouched();
            return;
        }

        this.isLoading = true;
        // Use getRawValue() to include disabled controls (ownerType, owner in dialog mode)
        const formValue = this.bankAccountEntryForm.getRawValue();
        const payload: BankAccount = {
            accountName: formValue.accountName,
            accountNumber: formValue.accountNumber,
            iban: formValue.iban,
            currencyCode: formValue.currencyCode,
            bank: formValue.bank, // This should be the Bank object
            party: this.party,
            id: 0,
            account: new ChartOfAccounts,
            owningCompanyId: this.sessionStorageService.getCompany()?.id ?? 0
        };

        const saveOperation = this.editMode
            ? this.bankAccountService.updateBankAccount(this.bankAccountId!, { ...payload, id: this.bankAccountId! })
            : this.bankAccountService.createBankAccount(payload);

        saveOperation.pipe(
            finalize(() => this.isLoading = false)
        ).subscribe({
            next: (savedAccount: BankAccount) => { // Assuming service returns the saved account
                if (this.isDialog) {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Bank account created successfully.' });
                    this.dialogRef.close(savedAccount); // Pass the new account back to the caller
                } else {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: `Bank account ${this.editMode ? 'updated' : 'created'} successfully.` });
                    this.router.navigate(['/accounts/bank-accounts']); // Navigate to the list page
                }
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save bank account.' });
                console.error('Save bank account error:', err);
            }
        });
    }

    cancel(): void {
        if (this.isDialog) {
            this.dialogRef.close();
        } else {
            this.router.navigate(['/accounts/bank-accounts']);
        }
    }

}
